'use client'

import type {LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage, PrismicNextLink} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {HeadingSectionSlice} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {getLinkLocale} from 'src/common/helpers'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

type Props = {
  slice: HeadingSectionSlice
  pageUid: string
  componentIndex: number
  primaryButtonLink: LinkField
  secondaryButtonLink: LinkField
  primaryButtonDocumentLabel?: string
  secondaryButtonDocumentLabel?: string
}

const bgMap: Record<string, string> = {
  'Dark mode': 'bg-green-dark',
  'Light mode grey': 'bg-moss-grey-100',
  'Light mode white': 'bg-moss-white',
  Transparent: 'bg-transparent',
}

const descriptionWidthMap: Record<string, string> = {
  'Small (400px)': 'max-w-[400px]',
  'Medium (520px)': 'max-w-[520px]',
  'Large (640px)': 'max-w-[640px]',
}

const getLinkTarget = (link: LinkField): string | undefined =>
  isFilled.link(link) && link.link_type === 'Web' && link.target ? link.target : undefined

const getLinkLocaleProps = (link: LinkField): {locale?: string} =>
  isFilled.link(link) && link.link_type === 'Document' && link.lang ? {locale: link.lang} : {}

export const HeadingSection = ({
  slice,
  pageUid,
  componentIndex,
  primaryButtonLink,
  secondaryButtonLink,
  primaryButtonDocumentLabel,
  secondaryButtonDocumentLabel,
}: Props) => {
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()
  const primary = slice.primary

  const isDark = primary.background === 'Dark mode'
  const isLightGrey = primary.background === 'Light mode grey'
  const isCenter = primary.text_alignment !== 'Left'

  const bgClass = bgMap[primary.background ?? 'Light mode white'] ?? 'bg-moss-white'
  const paddingTopClass = PADDING_TOP_CLASS[primary.padding_top ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[primary.padding_bottom ?? DEFAULT_PADDING] ?? ''
  const descWidthClass = descriptionWidthMap[primary.description_width ?? 'Small (400px)'] ?? 'max-w-[400px]'

  const buttonHref = isFilled.link(primaryButtonLink) ? linkResolver(primaryButtonLink, 'heading-section') : undefined
  const buttonTarget = getLinkTarget(primaryButtonLink)
  const buttonLocale = getLinkLocale(primaryButtonLink, locale)

  const secondaryButtonHref = isFilled.link(secondaryButtonLink)
    ? linkResolver(secondaryButtonLink, 'heading-section')
    : undefined

  const secondaryButtonTarget = getLinkTarget(secondaryButtonLink)
  const secondaryButtonLocale = getLinkLocale(secondaryButtonLink, locale)

  const eyebrowDesign = primary.eyebrow_design ?? 'Regular caps'
  const hasBadgeBg = eyebrowDesign.startsWith('Solid pill')
  const hasBadgeIcon = eyebrowDesign.startsWith('Badge icon')
  const isNeon = eyebrowDesign.includes('neon')
  const isPurple = eyebrowDesign.includes('purple')
  const isWhite = eyebrowDesign.includes('white')

  const hasEyebrow = isFilled.keyText(primary.eyebrow_text)
  const hasHeading = isFilled.richText(primary.heading)
  const hasSubtext = isFilled.keyText(primary.under_heading_copy)
  const hasDescriptionRichText = isFilled.richText(primary.description_rich_text)

  const buttonLabel = isFilled.keyText(primary.button_text)
    ? primary.button_text
    : primaryButtonDocumentLabel || undefined

  const hasButton = buttonLabel && buttonHref

  const secondaryButtonLabel = isFilled.keyText(primary.secondary_button_text)
    ? primary.secondary_button_text
    : secondaryButtonDocumentLabel || undefined

  const hasSecondaryButton = secondaryButtonLabel && secondaryButtonHref

  const descriptionColorClass = cn({
    'text-moss-white/70': isDark,
    'text-moss-grey-500': isLightGrey,
    'text-grey-550': !isDark && !isLightGrey,
  })

  if (!hasEyebrow && !hasHeading && !hasSubtext && !hasDescriptionRichText && !hasButton && !hasSecondaryButton) {
    return null
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, paddingTopClass, paddingBottomClass)}
    >
      <div
        className={cn('mx-auto max-w-[1280px] px-5 pt-8 md:px-10 lg:pt-16', {
          'text-center': isCenter,
          'text-left': !isCenter,
        })}
      >
        {hasEyebrow && (
          <div
            className={cn('mb-4 flex', {
              'justify-center': isCenter,
              'justify-start': !isCenter,
            })}
          >
            {hasBadgeBg && (
              <span
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest',
                  {
                    'bg-green-neon text-green-dark': isNeon,
                    'bg-moss-purple text-moss-white': isPurple,
                    'bg-moss-white text-green-dark': isWhite,
                  },
                )}
              >
                {isFilled.image(primary.eyebrow_icon) && (
                  <PrismicNextImage
                    field={primary.eyebrow_icon}
                    className={cn('size-5', {
                      'brightness-0': isNeon || isWhite,
                      'brightness-0 invert': isPurple,
                    })}
                  />
                )}
                {primary.eyebrow_text}
              </span>
            )}
            {hasBadgeIcon && (
              <span
                className={cn('inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-widest', {
                  'text-moss-grey-300': isDark,
                  'text-moss-grey-500': !isDark,
                })}
              >
                {isFilled.image(primary.eyebrow_icon) && (
                  <span
                    className={cn('inline-flex size-8 items-center justify-center rounded-full', {
                      'bg-green-neon': isNeon,
                      'bg-gradient-to-br from-[#D9ABFF] to-moss-purple': isPurple,
                      'bg-moss-white': isWhite,
                    })}
                  >
                    <PrismicNextImage
                      field={primary.eyebrow_icon}
                      className={cn('size-4', {
                        'brightness-0': isNeon || isWhite,
                        'brightness-0 invert': isPurple,
                      })}
                    />
                  </span>
                )}
                {primary.eyebrow_text}
              </span>
            )}
            {!hasBadgeBg && !hasBadgeIcon && (
              <span
                className={cn('inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest', {
                  'text-green-neon': isDark,
                  'text-green-deep': !isDark,
                })}
              >
                {primary.eyebrow_text}
              </span>
            )}
          </div>
        )}

        {hasHeading && (
          <div
            className={cn('mx-auto', {
              'max-w-[800px]': isCenter,
            })}
          >
            <PrismicRichText
              field={primary.heading}
              components={{
                heading2: ({children}) => (
                  <h2
                    className={cn(
                      'text-40 font-normal leading-[1.1] tracking-[-0.4px] md:text-[56px] md:tracking-[-0.56px]',
                      isDark ? 'text-moss-grey-300' : 'text-grey-550',
                    )}
                  >
                    {children}
                  </h2>
                ),
                strong: ({children}) => (
                  <strong className={cn('font-normal', isDark ? 'text-moss-white' : 'text-moss-black')}>
                    {children}
                  </strong>
                ),
              }}
            />
          </div>
        )}

        {hasSubtext && (
          <p
            className={cn(
              'mt-4 whitespace-pre-line text-16 leading-6 tracking-[0.16px]',
              descWidthClass,
              descriptionColorClass,
              {'mx-auto': isCenter},
            )}
          >
            {primary.under_heading_copy}
          </p>
        )}

        {hasDescriptionRichText && (
          <div
            className={cn('mt-4 text-16 leading-6 tracking-[0.16px]', descWidthClass, descriptionColorClass, {
              'mx-auto': isCenter,
            })}
          >
            <PrismicRichText
              field={primary.description_rich_text}
              components={{
                hyperlink: ({node, children}) => (
                  <PrismicNextLink
                    href={linkResolver(node.data, 'heading-section')}
                    {...getLinkLocaleProps(node.data)}
                    target={getLinkTarget(node.data)}
                    rel={getLinkTarget(node.data) ? 'noopener noreferrer' : undefined}
                    className="underline underline-offset-2 hover:opacity-80"
                  >
                    {children}
                  </PrismicNextLink>
                ),
              }}
            />
          </div>
        )}

        {(hasButton || hasSecondaryButton) && (
          <div
            className={cn('mt-8 flex flex-wrap gap-3', {
              'justify-center': isCenter,
              'justify-start': !isCenter,
            })}
          >
            {hasButton && (
              <ButtonV2
                variant="primary"
                href={buttonHref}
                target={buttonTarget}
                locale={buttonLocale}
                trackingData={{
                  location: `component-${componentIndex}-heading-section`,
                  dataType: 'primary-button',
                  mktgId: '',
                  pageName: pageUid,
                  label: buttonLabel ?? '',
                }}
              >
                {buttonLabel}
              </ButtonV2>
            )}
            {hasSecondaryButton && (
              <ButtonV2
                variant="secondary"
                href={secondaryButtonHref}
                target={secondaryButtonTarget}
                locale={secondaryButtonLocale}
                trackingData={{
                  location: `component-${componentIndex}-heading-section`,
                  dataType: 'secondary-button',
                  mktgId: '',
                  pageName: pageUid,
                  label: secondaryButtonLabel ?? '',
                }}
              >
                {secondaryButtonLabel}
              </ButtonV2>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
